import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Svg,
  Line,
  Circle,
  Rect,
  Path,
  G,
} from '@react-pdf/renderer';
import { RoastProfileLogo } from './RoastProfileLogo';
import type {
  RoastChartPoint,
  RoastData,
  RoastPhaseArea,
  RoastProfileRow,
  RoastSeriesKey,
  RoastStage,
  RoastStageMarker,
  RoastStageState,
} from './types';
import {
  calculateChartData,
  calculatePhaseAreas,
  calculateXTicks,
  calculateTempTicks,
  calculateStageMarkers,
  getAgtronClassification,
} from './RoastProfileUtils';

const styles = StyleSheet.create({
  page: {
    paddingTop: 15,
    paddingBottom: 20,
    paddingHorizontal: 30,
    fontSize: 9,
    color: '#1f2937',
    fontFamily: 'Helvetica',
    backgroundColor: '#ffffff',
  },
  // --- HEADER SECTION ---
  title: {
    fontSize: 17,
    textAlign: 'center',
    fontWeight: 'bold',
    color: '#111827',
    letterSpacing: -0.5,
  },
  subtitle: {
    fontSize: 10,
    textAlign: 'center',
    marginBottom: 15,
    color: '#6b7280',
  },

  headerContainer: {
    border: '0.8pt solid #e5e7eb',
    borderRadius: 8,
    padding: 12,
    marginBottom: 15,
    marginTop: 10,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  headerGridCol: {
    width: '32%',
  },
  headerInfoItem: {
    marginBottom: 4,
  },
  headerTerm: {
    fontSize: 7,
    color: '#9ca3af',
    marginBottom: 2,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 'bold',
  },
  headerVal: {
    fontSize: 7,
    color: '#111827',
    fontWeight: 'bold',
  },
  // --- CHART & GRID ---
  chartContainer: {
    border: '1pt solid #f3f4f6',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 3,
    marginBottom: 10,
    backgroundColor: '#ffffff',
  },
  // --- TABLES ---
  tableLogoHeader: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 2,
    color: '#374151',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  table: {
    width: '100%',
    borderRadius: 6,
    overflow: 'hidden',
    border: '1pt solid #9ca3af',
  },
  tableRow: {
    flexDirection: 'row',
    borderBottom: '0.5pt solid #d1d5db',
    minHeight: 18,
    alignItems: 'center',
  },
  tableHeader: {
    backgroundColor: '#f3f4f6',
    borderBottom: '1pt solid #9ca3af',
    fontWeight: 'bold',
  },
  tableCell: {
    padding: 4,
    textAlign: 'center',
    flex: 1,
    fontSize: 7,
    color: '#4b5563',
  },
  // --- COMPACT ANALYSIS ROW ---
  compactAnalysisRow: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: 10,
    borderTop: '0.5pt solid #f3f4f6',
  },
  compactMetricGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 16,
  },
  compactMetric: {
    flexDirection: 'column',
    gap: 2,
  },
  compactMetricLabel: {
    fontSize: 6,
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 'bold',
  },
  compactMetricValueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
  },
  compactMetricValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#111827',
  },
  compactMetricUnit: {
    fontSize: 7,
    color: '#6b7280',
    fontWeight: 'normal',
    marginLeft: 1,
  },
  compactSubtext: {
    fontSize: 6,
    color: '#6366f1',
    fontWeight: 'normal',
  },
  compactDivider: {
    width: '0.5pt',
    height: 16,
    backgroundColor: '#e5e7eb',
    marginHorizontal: 16,
  },
  // --- LANDSCAPE LAYOUT ---
  flexRow: {
    flexDirection: 'row',
    gap: 15,
  },
  leftCol: {
    width: '29%',
  },
  rightCol: {
    width: '69%',
  },
  notes: {
    backgroundColor: '#f9fafb',
    borderRadius: 8,
    padding: 8,
    marginTop: 8,
    borderLeft: '3pt solid #d1d5db',
  },
});

const SERIES_CONFIG: Record<RoastSeriesKey, { color: string; label: string; axis: 'left' | 'right'; type?: 'stepAfter' }> = {
  bean: { color: '#16a34a', label: 'Bean Temp', axis: 'left' },
  ror: { color: '#eab308', label: 'ROR', axis: 'right' },
  fire: { color: '#dc2626', label: 'Fire', axis: 'left', type: 'stepAfter' },
  airflow: { color: '#2563eb', label: 'Airflow', axis: 'left', type: 'stepAfter' },
  airTemp: { color: '#0ea5e9', label: 'Air Temp', axis: 'left' },
  drumTemp: { color: '#78350f', label: 'Drum Temp', axis: 'left' },
  environmentTemp: { color: '#a855f7', label: 'Environment Temp', axis: 'left' },
  drumRpm: { color: '#f97316', label: 'Drum RPM', axis: 'right' },
};

interface PDFChartProps {
  chartData: RoastChartPoint[];
  phaseAreas: RoastPhaseArea[];
  stageMarkers: RoastStageMarker[];
  xTicks: number[];
  tempTicks: number[];
  rightAxisMax: number;
  availableSeries: RoastSeriesKey[];
  width?: number;
  height?: number;
}

const buildMonotonePath = (points: { x: number; y: number }[]): string => {
  const n = points.length;
  if (n === 0) return '';
  if (n === 1) return `M${points[0].x},${points[0].y}`;
  if (n === 2) return `M${points[0].x},${points[0].y} L${points[1].x},${points[1].y}`;

  const dx: number[] = new Array(n - 1);
  const slope: number[] = new Array(n - 1);
  for (let i = 0; i < n - 1; i++) {
    dx[i] = points[i + 1].x - points[i].x;
    slope[i] = dx[i] !== 0 ? (points[i + 1].y - points[i].y) / dx[i] : 0;
  }

  const m: number[] = new Array(n);
  m[0] = slope[0];
  m[n - 1] = slope[n - 2];
  for (let i = 1; i < n - 1; i++) {
    m[i] = slope[i - 1] * slope[i] <= 0 ? 0 : (slope[i - 1] + slope[i]) / 2;
  }

  for (let i = 0; i < n - 1; i++) {
    if (slope[i] === 0) {
      m[i] = 0;
      m[i + 1] = 0;
    } else {
      const a = m[i] / slope[i];
      const b = m[i + 1] / slope[i];
      const h = a * a + b * b;
      if (h > 9) {
        const tau = 3 / Math.sqrt(h);
        m[i] = tau * a * slope[i];
        m[i + 1] = tau * b * slope[i];
      }
    }
  }

  let path = `M${points[0].x},${points[0].y}`;
  for (let i = 0; i < n - 1; i++) {
    const cx1 = points[i].x + dx[i] / 3;
    const cy1 = points[i].y + (m[i] * dx[i]) / 3;
    const cx2 = points[i + 1].x - dx[i] / 3;
    const cy2 = points[i + 1].y - (m[i + 1] * dx[i]) / 3;
    path += ` C${cx1},${cy1} ${cx2},${cy2} ${points[i + 1].x},${points[i + 1].y}`;
  }
  return path;
};

const buildStepAfterPath = (points: { x: number; y: number }[]): string => {
  if (points.length === 0) return '';
  let path = `M${points[0].x},${points[0].y}`;
  for (let i = 1; i < points.length; i++) {
    path += ` L${points[i].x},${points[i - 1].y} L${points[i].x},${points[i].y}`;
  }
  return path;
};

const PDFChart = ({ chartData, phaseAreas, stageMarkers, xTicks, tempTicks, rightAxisMax, availableSeries, width = 450, height = 400 }: PDFChartProps) => {
  const padding = { top: 35, right: 25, bottom: 30, left: 25 };

  const innerW = width - padding.left - padding.right;
  const innerH = height - padding.top - padding.bottom;

  const maxTime = xTicks[xTicks.length - 1] || 1;
  const maxTemp = tempTicks[tempTicks.length - 1] || 250;
  const minTemp = -5;

  const getX = (t: number) => padding.left + (t / maxTime) * innerW;
  const getY = (v: number) => padding.top + innerH - ((v - minTemp) / (maxTemp - minTemp)) * innerH;
  const getYRight = (v: number) => padding.top + innerH - (v / rightAxisMax) * innerH;

  // Calculate centered legend items
  const legendSpacing = 50;
  const legendY = 15;
  const totalLegendWidth = availableSeries.length * legendSpacing;
  const legendStartX = (width - totalLegendWidth) / 2 + 5;

  return (
    <Svg width={width} height={height}>
      {/* Legend - Centered */}
      {availableSeries.map((s, idx) => (
        <G key={s} transform={`translate(${legendStartX + idx * legendSpacing}, ${legendY})`}>
          <Rect x={0} y={0} width={6} height={6} fill={SERIES_CONFIG[s]?.color || '#ccc'} />
          <Text x={8} y={5} style={{ fontSize: 6, fill: '#000000' }}>{SERIES_CONFIG[s]?.label || s}</Text>
        </G>
      ))}

      {/* Background Phasing */}
      {phaseAreas.map((area, i) => {
        const x1 = getX(area.x1);
        const x2 = getX(area.x2);
        const width = Math.max(0, x2 - x1);
        const centerX = x1 + width / 2;
        
        return (
          <G key={i}>
            <Rect
              x={x1}
              y={padding.top}
              width={width}
              height={innerH}
              fill={area.fill}
              fillOpacity={0.15}
            />
            {width > 30 && (
              <G>
                <Text
                  x={centerX}
                  y={padding.top + 10}
                  style={{ fontSize: 7, textAnchor: 'middle', fill: '#000000', fontWeight: 'bold' }}
                >
                  {area.label}
                </Text>
                <Text
                  x={centerX}
                  y={padding.top + 20}
                  style={{ fontSize: 6, textAnchor: 'middle', fill: '#000000' }}
                >
                  {area.detail}
                </Text>
              </G>
            )}
          </G>
        );
      })}

      {/* Grid Lines */}
      {tempTicks.map((t: number, i: number) => (
        <Line
          key={i}
          x1={padding.left}
          y1={getY(t)}
          x2={padding.left + innerW}
          y2={getY(t)}
          stroke="#f3f4f6"
          strokeWidth={0.5}
        />
      ))}

      {/* Axes */}
      <Line x1={padding.left} y1={padding.top} x2={padding.left} y2={padding.top + innerH} stroke="#e5e7eb" />
      <Line x1={padding.left} y1={padding.top + innerH} x2={padding.left + innerW} y2={padding.top + innerH} stroke="#e5e7eb" />
      <Line x1={padding.left + innerW} y1={padding.top} x2={padding.left + innerW} y2={padding.top + innerH} stroke="#e5e7eb" />

      {/* Curves */}
      {availableSeries.map((sKey) => {
        const cfg = SERIES_CONFIG[sKey];
        const isStep = cfg?.type === 'stepAfter';
        const useRight = cfg?.axis === 'right';

        const rawPoints: { x: number; y: number; v: number }[] = [];
        for (const point of chartData) {
          const v = point[sKey];
          if (v === null || v === undefined) continue;
          rawPoints.push({
            x: getX(point.time),
            y: useRight ? getYRight(v) : getY(v),
            v,
          });
        }

        let points = rawPoints.map((p) => ({ x: p.x, y: p.y }));

        if (isStep) {
          const condensed: { x: number; y: number }[] = [];
          for (let i = 0; i < rawPoints.length; i++) {
            const prev = rawPoints[i - 1];
            const curr = rawPoints[i];
            const next = rawPoints[i + 1];
            const isFirst = i === 0;
            const isLast = i === rawPoints.length - 1;
            const changedFromPrev = !prev || prev.v !== curr.v;
            const changesNext = !!next && next.v !== curr.v;
            if (isFirst || isLast || changedFromPrev || changesNext) {
              condensed.push({ x: curr.x, y: curr.y });
            }
          }
          points = condensed;
        }

        const d = isStep ? buildStepAfterPath(points) : buildMonotonePath(points);

        return (
          <Path
            key={sKey}
            d={d}
            stroke={cfg?.color || '#333'}
            strokeWidth={sKey === 'bean' ? 1.5 : 1}
            fill="none"
          />
        );
      })}

      {/* Step Labels */}
      {availableSeries.filter(s => SERIES_CONFIG[s]?.type === 'stepAfter').map(sKey => {
         const labels = [];
         let lastValidValue: number | null = null;

         for (let i = 0; i < chartData.length; i++) {
            const v = chartData[i][sKey];
            if (v === null || v === undefined) continue;

            const isFirstValid = lastValidValue === null;
            const isChanged = isFirstValid || v !== lastValidValue;
            const isDrop = !isFirstValid && lastValidValue !== null && v < lastValidValue;

            let isLastValid = true;
            for (let j = i + 1; j < chartData.length; j++) {
              if (chartData[j][sKey] !== null && chartData[j][sKey] !== undefined) {
                 isLastValid = false;
                 break;
              }
            }

            if (isChanged || isLastValid) {
               labels.push({ x: getX(chartData[i].time), y: getY(v), value: v, isFirstValid, isDrop, isLastValid, i });
            }
            lastValidValue = v;
         }

         return labels.map((l, idx) => {
            let dx = 0;
            let dy = 0;
            let textAnchor: 'start' | 'middle' | 'end' = 'middle';

            if (l.isFirstValid) { dx = -3; dy = 2; textAnchor = 'end'; }
            else if (l.isLastValid) { dx = 3; dy = 2; textAnchor = 'start'; }
            else if (l.isDrop) { dx = 3; dy = 6; textAnchor = 'start'; }
            else { dx = 3; dy = -3; textAnchor = 'start'; }

            return (
              <Text key={`${sKey}-lbl-${idx}`} x={l.x + dx} y={l.y + dy} style={{ fontSize: 6, fontWeight: 'bold', fill: SERIES_CONFIG[sKey].color, textAnchor }}>
                {l.value}
              </Text>
            );
         });
      })}

      {/* Stage Markers */}
      {stageMarkers.map((m, i) => {
        const cx = getX(m.time);
        const cy = getY(m.temp);
        const dx = 6;
        const dyTop = 14;
        const dyBottom = 14;
        return (
          <G key={`marker-${i}`}>
            <Circle cx={cx} cy={cy} r={2} fill="#111" stroke="#fff" strokeWidth={0.5} />
            {m.labelTop && (
              <G>
                <Path d={`M${cx},${cy} L${cx + dx},${cy - dyTop}`} stroke="#111" strokeWidth={0.5} fill="none" />
                <Text x={cx + dx} y={cy - dyTop - 2} style={{ fontSize: 6, textAnchor: 'middle', fill: '#111', fontWeight: 'bold' }}>
                  {m.labelTop}
                </Text>
              </G>
            )}
            {m.labelBottom && (
              <G>
                <Path d={`M${cx},${cy} L${cx + dx},${cy + dyBottom}`} stroke="#111" strokeWidth={0.5} fill="none" />
                <Text x={cx + dx} y={cy + dyBottom + 6} style={{ fontSize: 6, textAnchor: 'middle', fill: '#111', fontWeight: 'bold' }}>
                  {m.labelBottom}
                </Text>
              </G>
            )}
          </G>
        );
      })}

      {/* Axis Labels */}
      {tempTicks.map((t, i) => (
        <Text key={`temp-${i}`} x={padding.left - 5} y={getY(t) + 2} style={{ fontSize: 6, textAnchor: 'end', fill: '#000000' }}>{t}°</Text>
      ))}
      <Text x={padding.left + innerW + 5} y={padding.top - 5} style={{ fontSize: 6, textAnchor: 'start', fill: '#000000' }}>{rightAxisMax}</Text>
      <Text x={padding.left + innerW + 5} y={padding.top + innerH + 2} style={{ fontSize: 6, textAnchor: 'start', fill: '#000000' }}>0</Text>

      {/* Vertical grid lines at X ticks */}
      {xTicks.map((t, i) => (
        <Line
          key={`xgrid-${i}`}
          x1={getX(t)}
          y1={padding.top}
          x2={getX(t)}
          y2={padding.top + innerH}
          stroke="#535558"
          strokeWidth={0.5}
          strokeDasharray="2,2"
          strokeOpacity={0.3} 
        />
      ))}

      {xTicks.map((t, i) => (
        <G key={`xtick-${i}`} transform={`translate(${getX(t)}, ${padding.top + innerH + 8}) rotate(45)`}>
          <Text x={0} y={0} style={{ fontSize: 6, textAnchor: 'start', fill: '#000000' }}>
            {Math.floor(t)}:{String(Math.round((t % 1) * 60)).padStart(2, '0')}
          </Text>
        </G>
      ))}
    </Svg>
  );
};

interface RoastProfilePDFProps {
  recipeName?: string;
  roastName: string;
  machineName: string;
  beanName: string;
  date: string;
  time: string;
  roastData: RoastData;
  roastType?: string;
  notes?: string;
  actualEnd?: string;
  roaster?: string;
  lotName?: string;
}

export const RoastProfilePDF = ({ recipeName, roastName, machineName, beanName, roastData, roastType, notes, actualEnd, roaster, lotName }: RoastProfilePDFProps) => {
  const stageLabelByKey: Record<RoastStageState['key'], string> = {
    start: 'Start',
    turning_point: 'Turning Point',
    yellow: 'Yellow',
    first_crack: 'First Crack',
    end: 'End',
  };

  const toStageStates = (stages: RoastStage[]): RoastStageState[] => {
    return stages.map((s) => ({
      key: s.name,
      label: stageLabelByKey[s.name],
      time: s.time,
      temp: s.temp,
    }));
  };

  const currentStages = roastData.stageStates ?? toStageStates(roastData.stages);
  const chartData = calculateChartData(roastData.profile, currentStages);
  const maxTime = chartData.length > 0 ? chartData[chartData.length - 1].time : 0;
  const phaseAreas = calculatePhaseAreas(currentStages, maxTime);
  const stageMarkers = calculateStageMarkers(currentStages);
  const xTicks = calculateXTicks(chartData, currentStages);
  
  const allSeriesKeys = Object.keys(SERIES_CONFIG) as RoastSeriesKey[];
  const availableSeries = allSeriesKeys.filter((key) => roastData.profile.some((r: RoastProfileRow) => r[key] != null));

  let maxTempFound = 0;
  // Check all available series that are temperatures (left axis, excluding fire/airflow)
  roastData.profile.forEach(r => {
    availableSeries.forEach(sKey => {
      const config = SERIES_CONFIG[sKey];
      const isTemperature = sKey === 'bean' || (config?.axis === 'left' && sKey !== 'fire' && sKey !== 'airflow');
      if (isTemperature) {
        const v = r[sKey];
        if (v != null && v > maxTempFound) maxTempFound = v;
      }
    });
  });
  // Also check stage temperatures
  currentStages.forEach(s => {
    if (s.temp !== '') {
      const v = Number(s.temp);
      if (!isNaN(v) && v > maxTempFound) maxTempFound = v;
    }
  });

  const tempAxisMax = Math.ceil((maxTempFound + 60) / 20) * 20 || 250;
  const tempTicks = calculateTempTicks(tempAxisMax);

  type RorCellMeta =
    | { kind: 'carrier'; ror: number | null }
    | { kind: 'covered' }
    | { kind: 'empty' };

  const rorMeta: RorCellMeta[] = (() => {
    const meta: RorCellMeta[] = new Array(roastData.profile.length);
    let nonManualIdx = -1;
    let coveredUntilExclusive = -1;

    for (let i = 0; i < roastData.profile.length; i++) {
      const isManual = roastData.profile[i].seconds % 30 !== 0;
      if (!isManual) nonManualIdx += 1;

      if (i < coveredUntilExclusive) {
        meta[i] = { kind: 'covered' };
        continue;
      }

      if (!isManual && nonManualIdx % 2 !== 0 && nonManualIdx > 0) {
        let span = 1;
        for (let j = i + 1; j < roastData.profile.length; j++) {
          span += 1;
          if (roastData.profile[j].seconds % 30 === 0) break;
        }
        meta[i] = { kind: 'carrier', ror: roastData.profile[i].ror ?? null };
        coveredUntilExclusive = i + span;
      } else {
        meta[i] = { kind: 'empty' };
      }
    }
    return meta;
  })();
  
  let maxRorFound = 5;
  roastData.profile.forEach(r => {
    if (r.ror && r.ror > maxRorFound) maxRorFound = r.ror;
    if (r.drumRpm && r.drumRpm > maxRorFound) maxRorFound = r.drumRpm;
  });
  const rightAxisMax = Math.ceil(maxRorFound / 10) * 10 + 10 || 40;

  const wIn = Number(roastData.weightInput || 0);
  const wOut = Number(roastData.weightOutput || 0);
  const shrinkage = (wIn > 0 && wOut > 0) ? (((wIn - wOut) / wIn) * 100).toFixed(2) : '0.00';

  const agtronWhole = Number(roastData.agtron.wholeBean || 0);
  const agtronGround = Number(roastData.agtron.groundBean || 0);
  const agtronDiff = (agtronWhole > 0 && agtronGround > 0) ? Math.abs(agtronWhole - agtronGround).toFixed(1) : '-';
  
  // Calculate classification dynamically for both whole and ground
  const classWhole = getAgtronClassification(roastData.agtron.wholeBean);
  const classGround = getAgtronClassification(roastData.agtron.groundBean);

  return (
    <Document title={`Roast Profile - ${roastName}`}>
      <Page size="A4" orientation="landscape" style={styles.page} wrap={false}>
        <View style={{ marginBottom: 12 }}>
          <RoastProfileLogo />
        </View>
        <View style={styles.flexRow}>
          {/* LEFT COLUMN: Data Log - Starts from very top */}
          <View style={styles.leftCol}>
            <View style={{ marginBottom: 10 }}>
              <Text style={styles.tableLogoHeader}>Roast Data Log</Text>
              <Text style={[styles.subtitle, { marginBottom: 0, fontSize: 8 }]}> </Text>
            </View>
            {(() => {
              const otherKeys = availableSeries.filter(k => k !== 'bean' && k !== 'ror');
              const timeFlex = 15;
              const cellFlex = 85 / (2 + otherKeys.length);
              const rightFlex = otherKeys.length * cellFlex;

              type Group = { rows: RoastProfileRow[]; rorValue: number | null };
              const groups: Group[] = [];
              let gi = 0;
              while (gi < roastData.profile.length) {
                const cell = rorMeta[gi];
                if (cell.kind === 'carrier') {
                  const groupRows = [roastData.profile[gi]];
                  let j = gi + 1;
                  while (j < roastData.profile.length && rorMeta[j].kind === 'covered') {
                    groupRows.push(roastData.profile[j]);
                    j++;
                  }
                  groups.push({ rows: groupRows, rorValue: cell.ror });
                  gi = j;
                } else {
                  groups.push({ rows: [roastData.profile[gi]], rorValue: null });
                  gi++;
                }
              }

              return (
                <View style={styles.table}>
                  <View style={[styles.tableRow, styles.tableHeader]}>
                    <Text style={[styles.tableCell, { flex: timeFlex, textAlign: 'left', paddingLeft: 6 }]}>Time</Text>
                    <Text style={[styles.tableCell, { flex: cellFlex }]}>Temp</Text>
                    <Text style={[styles.tableCell, { flex: cellFlex }]}>ROR</Text>
                    {otherKeys.map(k => (
                      <Text key={k} style={[styles.tableCell, { flex: cellFlex }]}>{SERIES_CONFIG[k]?.label || k}</Text>
                    ))}
                  </View>
                  {groups.map((g, idx) => (
                    <View key={idx} wrap={false} style={{ flexDirection: 'row', borderBottom: '0.5pt solid #d1d5db' }}>
                      <View style={{ flex: timeFlex + cellFlex, flexDirection: 'column' }}>
                        {g.rows.map((r, ri) => (
                          <View key={ri} style={{
                            flexDirection: 'row',
                            minHeight: 18,
                            alignItems: 'center',
                            borderBottom: ri < g.rows.length - 1 ? '0.5pt solid #d1d5db' : undefined,
                            backgroundColor: r.seconds % 30 !== 0 ? '#fffbeb' : undefined,
                          }}>
                            <Text style={[styles.tableCell, { flex: timeFlex, textAlign: 'left', paddingLeft: 6 }]}>{r.time}</Text>
                            <Text style={[styles.tableCell, { flex: cellFlex }]}>{r.bean != null ? `${r.bean}°` : ''}</Text>
                          </View>
                        ))}
                      </View>
                      <View style={{ flex: cellFlex, justifyContent: 'center', alignItems: 'center', borderLeft: '0.5pt solid #d1d5db', borderRight: '0.5pt solid #d1d5db' }}>
                        <Text style={{ fontSize: 7, color: '#4b5563', textAlign: 'center' }}>
                          {g.rorValue != null ? g.rorValue : ''}
                        </Text>
                      </View>
                      {otherKeys.length > 0 && (
                        <View style={{ flex: rightFlex, flexDirection: 'column' }}>
                          {g.rows.map((r, ri) => (
                            <View key={ri} style={{
                              flexDirection: 'row',
                              minHeight: 18,
                              alignItems: 'center',
                              borderBottom: ri < g.rows.length - 1 ? '0.5pt solid #d1d5db' : undefined,
                              backgroundColor: r.seconds % 30 !== 0 ? '#fffbeb' : undefined,
                            }}>
                              {otherKeys.map(k => {
                                const isTemp = k.toLowerCase().includes('temp');
                                return (
                                  <Text key={k} style={[styles.tableCell, { flex: cellFlex }]}>
                                    {r[k] != null ? (isTemp ? `${r[k]}°` : r[k]) : ''}
                                  </Text>
                                );
                              })}
                            </View>
                          ))}
                        </View>
                      )}
                    </View>
                  ))}
                </View>
              );
            })()}
          </View>

          <View style={styles.rightCol}>
            <View style={{ marginBottom: 10, alignItems: 'center' }}>
               <Text style={[styles.tableLogoHeader, { textAlign: 'center' }]}>Roast Profile Report</Text>
               <Text style={[styles.subtitle, { textAlign: 'center', marginBottom: 0, fontSize: 8 }]}>{recipeName || roastName || 'Unnamed Roast'}</Text>
            </View>

            {/* Header Info Grid (3 columns) */}
            <View style={[styles.headerContainer, { marginTop: 0, padding: 8, marginBottom: 8 }]}>
              {/* Column 1: Bean & Lot */}
              <View style={[styles.headerGridCol, { width: '25%' }]}>
                <View style={styles.headerInfoItem}>
                  <Text style={styles.headerTerm}>Bean Item</Text>
                  <Text style={styles.headerVal}>{beanName || '-'}</Text>
                </View>
                {lotName && (
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Lot Used</Text>
                    <Text style={styles.headerVal}>{lotName}</Text>
                  </View>
                )}
              </View>

              {/* Column 2: Date, Machine, Roaster */}
              <View style={[styles.headerGridCol, { width: '28%', paddingLeft: 10 }]}>
                {actualEnd && (
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Date</Text>
                    <Text style={styles.headerVal}>{actualEnd}</Text>
                  </View>
                )}
                <View style={styles.headerInfoItem}>
                  <Text style={styles.headerTerm}>Machine</Text>
                  <Text style={styles.headerVal}>{machineName || '-'}</Text>
                </View>
                {roaster && (
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Roaster</Text>
                    <Text style={styles.headerVal}>{roaster}</Text>
                  </View>
                )}
              </View>

              {/* Column 3: Metrics (Weight & Agtron) */}
              <View style={{ width: '47%', flexDirection: 'row' }}>
                {/* Column 3.1: Weight */}
                <View style={{ width: '45%', paddingLeft: 10, borderLeft: '0.5pt solid #e5e7eb' }}>
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>In / Out (kg)</Text>
                    <Text style={styles.headerVal}>{wIn.toFixed(2)} / {wOut.toFixed(2)}</Text>
                  </View>
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Loss</Text>
                    <Text style={[styles.headerVal, { color: '#b91c1c' }]}>{shrinkage}%</Text>
                  </View>
                </View>

                {/* Column 3.2: Agtron */}
                <View style={{ width: '55%', paddingLeft: 10, borderLeft: '0.5pt solid #e5e7eb' }}>
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Whole Agtron</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 3 }}>
                      <Text style={styles.headerVal}>{roastData.agtron.wholeBean || '-'}</Text>
                      <Text style={{ fontSize: 6, color: '#000000' }}>{classWhole.scaa} / {classWhole.common}</Text>
                    </View>
                  </View>
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Ground Agtron</Text>
                    <View style={{ flexDirection: 'row', alignItems: 'baseline', gap: 3 }}>
                      <Text style={styles.headerVal}>{roastData.agtron.groundBean || '-'}</Text>
                      <Text style={{ fontSize: 6, color: '#000000' }}>{classGround.scaa} / {classGround.common}</Text>
                    </View>
                  </View>
                  <View style={styles.headerInfoItem}>
                    <Text style={styles.headerTerm}>Diff / Type</Text>
                    <Text style={styles.headerVal}>{agtronDiff} | {roastType}</Text>
                  </View>
                </View>
              </View>
            </View>

            <View style={[styles.chartContainer, { paddingBottom: 5 }]}>
              <Text style={styles.tableLogoHeader}>Roast Profile Curve</Text>
                <PDFChart
                  chartData={chartData}
                  phaseAreas={phaseAreas}
                  stageMarkers={stageMarkers}
                  xTicks={xTicks}
                  tempTicks={tempTicks}
                  rightAxisMax={rightAxisMax}
                  availableSeries={availableSeries}
                  width={520}
                  height={330}
                />


            </View>

            {notes && (
              <View style={styles.notes} wrap={false}>
                 <Text style={[styles.compactMetricLabel, { marginBottom: 4, color: '#6b7280' }]}>Notes</Text>
                 <Text style={{ fontSize: 7, color: '#4b5563', lineHeight: 1.1 }}>{notes}</Text>
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  );
};
